import React, { useState } from 'react';
import { Users, DollarSign, FileText, Calculator, TrendingUp, TrendingDown, AlertCircle, CheckCircle, Building, CreditCard } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar, ComposedChart } from 'recharts';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const FiscalData = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Données pour les graphiques de comparaison sur 5 ans
    const donneesFiscalesSociales = [
        { 
            annee: '2020', 
            effectifPersonnel: 12, 
            masseSalariale: 420000, 
            impotSocietes: 45000,
            autresImpotsTaxes: 23000,
            chargesSociales: 126000,
            tauxChargesSociales: 30
        },
        { 
            annee: '2021', 
            effectifPersonnel: 14, 
            masseSalariale: 485000, 
            impotSocietes: 52000,
            autresImpotsTaxes: 28000,
            chargesSociales: 145500,
            tauxChargesSociales: 30
        },
        { 
            annee: '2022', 
            effectifPersonnel: 16, 
            masseSalariale: 560000, 
            impotSocietes: 68000,
            autresImpotsTaxes: 35000,
            chargesSociales: 168000,
            tauxChargesSociales: 30
        },
        { 
            annee: '2023', 
            effectifPersonnel: 18, 
            masseSalariale: 635000, 
            impotSocietes: 78000,
            autresImpotsTaxes: 42000,
            chargesSociales: 190500,
            tauxChargesSociales: 30
        },
        { 
            annee: '2024', 
            effectifPersonnel: 20, 
            masseSalariale: 720000, 
            impotSocietes: 89000,
            autresImpotsTaxes: 48000,
            chargesSociales: 216000,
            tauxChargesSociales: 30
        }
    ];

    // Répartition des charges sociales par type
    const repartitionChargesSociales = [
        { name: 'Cotisations URSSAF', value: 45, color: '#10b981', montant: 97200 },
        { name: 'Cotisations retraite', value: 20, color: '#3b82f6', montant: 43200 },
        { name: 'Assurance chômage', value: 15, color: '#f59e0b', montant: 32400 },
        { name: 'Mutuelle/Prévoyance', value: 12, color: '#8b5cf6', montant: 25920 },
        { name: 'Formation professionnelle', value: 8, color: '#ef4444', montant: 17280 }
    ];

    // Évolution mensuelle 2024
    const evolutionMensuelle2024 = [
        { mois: 'Jan', masseSalariale: 58500, chargesSociales: 17550, impots: 7200 },
        { mois: 'Fév', masseSalariale: 59200, chargesSociales: 17760, impots: 7400 },
        { mois: 'Mar', masseSalariale: 60800, chargesSociales: 18240, impots: 7800 },
        { mois: 'Avr', masseSalariale: 58900, chargesSociales: 17670, impots: 7300 },
        { mois: 'Mai', masseSalariale: 61500, chargesSociales: 18450, impots: 8100 },
        { mois: 'Juin', masseSalariale: 62200, chargesSociales: 18660, impots: 8200 },
        { mois: 'Juil', masseSalariale: 59800, chargesSociales: 17940, impots: 7600 },
        { mois: 'Août', masseSalariale: 60100, chargesSociales: 18030, impots: 7650 }
    ];

    // Répartition des impôts et taxes
    const repartitionImpotsTaxes = [
        { type: 'Impôt sur les sociétés', pourcentage: 65, montant: 89000 },
        { type: 'Taxe professionnelle', pourcentage: 15, montant: 20550 },
        { type: 'Contribution économique', pourcentage: 10, montant: 13700 },
        { type: 'Taxes foncières', pourcentage: 6, montant: 8220 },
        { type: 'Autres taxes', pourcentage: 4, montant: 5480 }
    ];

    // Analyse comparative secteur
    const comparaisonSecteur = [
        { indicateur: 'Masse salariale/CA', entreprise: 28.5, secteur: 32.1 },
        { indicateur: 'Taux charges sociales', entreprise: 30.0, secteur: 31.2 },
        { indicateur: 'Effectif moyen/CA', entreprise: 0.8, secteur: 1.1 },
        { indicateur: 'Impôts/Résultat', entreprise: 25.2, secteur: 28.5 }
    ];

    // Données actuelles 2024
    const currentYear = {
        effectifPersonnel: 20,
        masseSalariale: 720000,
        impotSocietes: 89000,
        autresImpotsTaxes: 48000,
        chargesSociales: 216000,
        totalFiscalSocial: 353000,
        salaireAnnuelMoyen: 36000
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculatePercentage = (current, previous) => {
        if (!previous) return 0;
        return ((current - previous) / previous * 100).toFixed(1);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${entry.value > 1000 ? formatCurrency(entry.value) : entry.value}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomPercentTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${entry.value}%`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const getComparisonColor = (entreprise, secteur) => {
        return entreprise < secteur ? '#10b981' : '#ef4444';
    };

    return (
        <>
            <div className="app-container">
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

                <div className="main-wrapper">
                    <Navbar onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

                    <main className="main-content">
                        {/* Header Section */}
                        <div className="page-header">
                            <div className="header-content">
                                <div className="header-text">
                                    <h1 className="page-title">Données Fiscales & Sociales</h1>
                                    <p className="page-subtitle">Analyse des obligations fiscales et sociales de votre entreprise</p>
                                </div>
                            </div>
                        </div>

                        {/* KPIs Cards */}
                        <div className="kpis-grid">
                            <div className="kpi-card primary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Effectif du Personnel</h3>
                                        <p className="kpi-value">{currentYear.effectifPersonnel}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.effectifPersonnel, donneesFiscalesSociales[3].effectifPersonnel)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Users size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card success">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Masse Salariale</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.masseSalariale)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.masseSalariale, donneesFiscalesSociales[3].masseSalariale)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <DollarSign size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card info">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Impôt sur les Sociétés</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.impotSocietes)}</p>
                                        <span className="kpi-trend neutral">
                                            +{calculatePercentage(currentYear.impotSocietes, donneesFiscalesSociales[3].impotSocietes)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <FileText size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card warning">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Autres Impôts & Taxes</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.autresImpotsTaxes)}</p>
                                        <span className="kpi-trend neutral">
                                            +{calculatePercentage(currentYear.autresImpotsTaxes, donneesFiscalesSociales[3].autresImpotsTaxes)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Calculator size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card secondary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Charges Sociales</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.chargesSociales)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.chargesSociales, donneesFiscalesSociales[3].chargesSociales)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Building size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card danger">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Total Fiscal & Social</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.totalFiscalSocial)}</p>
                                        <span className="kpi-trend neutral">
                                            {((currentYear.totalFiscalSocial / 2520000) * 100).toFixed(1)}% du CA
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <CreditCard size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="charts-section">
                            {/* Évolution Effectif et Masse Salariale */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution de l'Effectif et de la Masse Salariale (5 ans)</h3>
                                    <div className="chart-badge">Croissance maîtrisée</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <ComposedChart data={donneesFiscalesSociales}>
                                            <defs>
                                                <linearGradient id="colorMasse" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                yAxisId="left"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Area
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="masseSalariale"
                                                name="Masse Salariale"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorMasse)"
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="effectifPersonnel"
                                                name="Effectif Personnel"
                                                stroke="#3b82f6"
                                                strokeWidth={4}
                                                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8 }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Évolution des Impôts et Taxes */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution des Impôts et Taxes</h3>
                                    <div className="chart-badge secondary">Fiscalité progressive</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <AreaChart data={donneesFiscalesSociales}>
                                            <defs>
                                                <linearGradient id="colorIS" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                                                </linearGradient>
                                                <linearGradient id="colorAutres" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Area
                                                type="monotone"
                                                dataKey="impotSocietes"
                                                name="Impôt sur les Sociétés"
                                                stackId="1"
                                                stroke="#3b82f6"
                                                fill="url(#colorIS)"
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="autresImpotsTaxes"
                                                name="Autres Impôts & Taxes"
                                                stackId="1"
                                                stroke="#f59e0b"
                                                fill="url(#colorAutres)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Évolution mensuelle 2024 */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution Mensuelle 2024</h3>
                                    <div className="chart-badge warning">Suivi régulier</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={evolutionMensuelle2024}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="mois"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="masseSalariale"
                                                name="Masse Salariale"
                                                fill="#10b981"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Bar
                                                dataKey="chargesSociales"
                                                name="Charges Sociales"
                                                fill="#8b5cf6"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Bar
                                                dataKey="impots"
                                                name="Impôts"
                                                fill="#ef4444"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Répartition des charges sociales */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition des Charges Sociales</h3>
                                    <div className="chart-badge success">Conforme aux normes</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={repartitionChargesSociales}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}%`}
                                                labelLine={false}
                                            >
                                                {repartitionChargesSociales.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value, name, props) => [
                                                `${value}% (${formatCurrency(props.payload.montant)})`,
                                                name
                                            ]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Comparaison avec le secteur */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Comparaison Sectorielle</h3>
                                    <div className="chart-badge info">Benchmarking</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={comparaisonSecteur} layout="horizontal">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                type="number"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="indicateur"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                width={120}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="entreprise"
                                                name="Votre Entreprise"
                                                fill="#10b981"
                                                radius={[0, 4, 4, 0]}
                                                opacity={0.8}
                                            />
                                            <Bar
                                                dataKey="secteur"
                                                name="Moyenne Secteur"
                                                fill="#3b82f6"
                                                radius={[0, 4, 4, 0]}
                                                opacity={0.8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Répartition des impôts et taxes */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition Détaillée des Impôts et Taxes</h3>
                                    <div className="chart-badge warning">Analyse fiscale</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={repartitionImpotsTaxes} layout="horizontal">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                type="number"
                                                domain={[0, 100]}
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${value}%`}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="type"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                width={150}
                                            />
                                            <Tooltip 
                                                formatter={(value, name, props) => [
                                                    `${value}% - ${formatCurrency(props.payload.montant)}`,
                                                    'Répartition'
                                                ]} 
                                            />
                                            <Bar
                                                dataKey="pourcentage"
                                                radius={[0, 8, 8, 0]}
                                                fill="#3b82f6"
                                                opacity={0.8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Commentaires du Cabinet */}
                        <div className="commentaires-section">
                            <div className="commentaires-card">
                                <div className="commentaires-header">
                                    <h3 className="commentaires-title">Commentaires du Cabinet CINEF-EC</h3>
                                    <div className="commentaires-date">
                                        Dernière mise à jour: {formatDate('2024-08-20')}
                                    </div>
                                </div>
                                <div className="commentaires-content">
                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Analyse de la Masse Salariale</h4>
                                        <p className="commentaire-text">
                                            L'effectif a progressé de {calculatePercentage(currentYear.effectifPersonnel, donneesFiscalesSociales[3].effectifPersonnel)}% 
                                            pour atteindre {currentYear.effectifPersonnel} salariés, avec une masse salariale de {formatCurrency(currentYear.masseSalariale)}. 
                                            Le salaire annuel moyen de {formatCurrency(currentYear.salaireAnnuelMoyen)} reste compétitif par rapport aux standards du marché.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Gestion Fiscale</h4>
                                        <p className="commentaire-text">
                                            L'impôt sur les sociétés s'élève à {formatCurrency(currentYear.impotSocietes)}, soit une progression de 
                                            {calculatePercentage(currentYear.impotSocietes, donneesFiscalesSociales[3].impotSocietes)}%. Cette évolution 
                                            témoigne de la croissance de la rentabilité. Les autres impôts et taxes représentent {formatCurrency(currentYear.autresImpotsTaxes)}, 
                                            restant dans des proportions maîtrisées.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Optimisation et Recommandations</h4>
                                        <p className="commentaire-text">
                                            Le taux de charges sociales de 30% de la masse salariale est conforme aux standards sectoriels. 
                                            Nous recommandons d'étudier les dispositifs d'aide à l'embauche disponibles et d'optimiser la répartition 
                                            des rémunérations pour réduire l'impact fiscal tout en maintenant l'attractivité employeur.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', sans-serif;
                    background-color: #f8fafc;
                    color: #1e293b;
                    line-height: 1.6;
                }

                .app-container {
                    display: flex;
                    min-height: 100vh;
                    
                }

                .main-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    margin-left: 320px;
                    z-index: 1;
                }

                .main-content {
                    flex: 1;
                    padding: 2rem;
                    overflow-y: auto;
                    background-color: #f8fafc;
                }

                /* Page Header */
                .page-header {
                    margin-bottom: 2rem;
                }

                .header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .page-title {
                    font-size: 36px;
                    font-weight: 800;
                    margin: 0 0 8px 0;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    line-height: 1.2;
                    letter-spacing: -0.8px;
                    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }   

                .page-subtitle {
                    font-size: 1.125rem;
                    color: #64748b;
                    font-weight: 500;
                }

                /* KPIs Grid */
                .kpis-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }

                .kpi-card {
                    background: white;
                    border-radius: 16px;
                    padding: 1.5rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    border: 1px solid #e2e8f0;
                }

                .kpi-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }

                .kpi-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    position: relative;
                    z-index: 2;
                }

                .kpi-info {
                    flex: 1;
                }

                .kpi-title {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #64748b;
                    margin-bottom: 0.5rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .kpi-value {
                    font-size: 1.875rem;
                    font-weight: 800;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .kpi-trend {
                    display: inline-flex;
                    align-items: center;
                    font-size: 0.875rem;
                    font-weight: 600;
                    padding: 0.25rem 0.75rem;
                    border-radius: 50px;
                }

                .kpi-trend.positive {
                    background-color: #dcfce7;
                    color: #166534;
                }

                .kpi-trend.negative {
                    background-color: #fef2f2;
                    color: #dc2626;
                }

                .kpi-trend.neutral {
                    background-color: #f1f5f9;
                    color: #475569;
                }

                .kpi-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 64px;
                    height: 64px;
                    border-radius: 16px;
                    opacity: 0.1;
                }

                .kpi-gradient {
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    opacity: 0.1;
                    z-index: 1;
                }

                .kpi-card.primary .kpi-gradient {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .kpi-card.primary .kpi-icon {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    opacity: 1;
                }

                .kpi-card.success .kpi-gradient {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }

                .kpi-card.success .kpi-icon {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    opacity: 1;
                }

                .kpi-card.info .kpi-gradient {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                }

                .kpi-card.info .kpi-icon {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                    opacity: 1;
                }

                .kpi-card.warning .kpi-gradient {
                    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
                }

                .kpi-card.warning .kpi-icon {
                    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
                    color: white;
                    opacity: 1;
                }

                .kpi-card.secondary .kpi-gradient {
                    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                }

                .kpi-card.secondary .kpi-icon {
                    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                    color: white;
                    opacity: 1;
                }

                .kpi-card.danger .kpi-gradient {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                }

                .kpi-card.danger .kpi-icon {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    color: white;
                    opacity: 1;
                }

                /* Charts Section */
                .charts-section {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
                    gap: 2rem;
                    margin-bottom: 2rem;
                }

                .chart-card {
                    background: white;
                    border-radius: 16px;
                    padding: 1.5rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                }

                .chart-card:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                }

                .chart-card.full-width {
                    grid-column: 1 / -1;
                }

                .chart-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 1rem;
                }

                .chart-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                }

                .chart-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.375rem 0.75rem;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    background-color: #10b981;
                    color: white;
                }

                .chart-badge.secondary {
                    background-color: #8b5cf6;
                }

                .chart-badge.warning {
                    background-color: #f59e0b;
                }

                .chart-badge.info {
                    background-color: #3b82f6;
                }

                .chart-badge.success {
                    background-color: #10b981;
                }

                .chart-content {
                    position: relative;
                }

                /* Custom Tooltip */
                .custom-tooltip {
                    background: white;
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    padding: 1rem;
                    border: 1px solid #e2e8f0;
                }

                .tooltip-label {
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                }

                .tooltip-value {
                    font-weight: 500;
                    font-size: 0.875rem;
                    margin: 0.25rem 0;
                }

                /* Commentaires Section */
                .commentaires-section {
                    margin-top: 2rem;
                }

                .commentaires-card {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e2e8f0;
                }

                .commentaires-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                    border-bottom: 2px solid #f1f5f9;
                    padding-bottom: 1rem;
                }

                .commentaires-title {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: #1e293b;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .commentaires-date {
                    font-size: 0.875rem;
                    color: #64748b;
                    font-weight: 500;
                    background-color: #f1f5f9;
                    padding: 0.5rem 1rem;
                    border-radius: 50px;
                }

                .commentaires-content {
                    display: flex;
                    flex-direction: column;
                    gap: 2rem;
                }

                .commentaire-item {
                    position: relative;
                    padding-left: 1rem;
                    border-left: 4px solid #10b981;
                }

                .commentaire-title {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 0.75rem;
                }

                .commentaire-text {
                    font-size: 1rem;
                    color: #64748b;
                    line-height: 1.7;
                    font-weight: 400;
                }

                /* Responsive Design */
                @media (max-width: 1024px) {
                    .main-content {
                        padding: 1.5rem;
                    }

                    .kpis-grid {
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 1rem;
                    }

                    .charts-section {
                        grid-template-columns: 1fr;
                        gap: 1.5rem;
                    }

                    .page-title {
                        font-size: 2rem;
                    }
                }

                @media (max-width: 768px) {
                    .main-content {
                        padding: 1rem;
                    }

                    .kpis-grid {
                        grid-template-columns: 1fr;
                    }

                    .kpi-value {
                        font-size: 1.5rem;
                    }

                    .page-title {
                        font-size: 1.75rem;
                    }

                    .commentaires-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: flex-start;
                    }

                    .chart-header {
                        flex-direction: column;
                        gap: 0.75rem;
                        align-items: flex-start;
                    }
                }

                @media (max-width: 640px) {
                    .kpi-content {
                        flex-direction: column;
                        text-align: center;
                        gap: 1rem;
                    }

                    .kpi-icon {
                        width: 48px;
                        height: 48px;
                    }

                    .chart-card {
                        padding: 1rem;
                    }

                    .commentaires-card {
                        padding: 1.5rem;
                    }
                }

                /* Animation and Effects */
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .kpi-card,
                .chart-card,
                .commentaires-card {
                    animation: slideIn 0.6s ease-out;
                }

                .kpi-card:nth-child(1) { animation-delay: 0.1s; }
                .kpi-card:nth-child(2) { animation-delay: 0.2s; }
                .kpi-card:nth-child(3) { animation-delay: 0.3s; }
                .kpi-card:nth-child(4) { animation-delay: 0.4s; }
                .kpi-card:nth-child(5) { animation-delay: 0.5s; }
                .kpi-card:nth-child(6) { animation-delay: 0.6s; }

                /* Print Styles */
                @media print {
                    .main-content {
                        background: white !important;
                    }
                    
                    .kpi-card,
                    .chart-card,
                    .commentaires-card {
                        box-shadow: none !important;
                        border: 1px solid #e2e8f0 !important;
                        page-break-inside: avoid;
                    }
                }
            `}</style>
        </>
    );
};
export default FiscalData;